import { useState } from 'react';
import {
    ArrowBack as BackIcon,
    RocketLaunchOutlined as LaunchIcon,
    SaveOutlined as SaveIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useCreateAutomation, useUpdateAutomation, useAutomationLimit } from '@/hooks/queries/dm-automation';
import { dialogOpen } from '@/modules/MuiDialog';
import type { DmAutomationDto } from '@/types/dmAutomation';
import { ACCENT, ACCENT_HOVER, PageHeader } from './_shared';
import TriggerExplainer from './TriggerExplainer';
import Step1Trigger from './wizard/Step1Trigger';
import Step2Name from './wizard/Step2Name';
import Step3Keywords from './wizard/Step3Keywords';
import Step4Target from './wizard/Step4Target';
import Step5Message from './wizard/Step5Message';
import {
    EMPTY_WIZARD_DATA,
    buildCreatePayload,
    buildUpdatePayload,
    dtoToWizardData,
    isStepApplicable,
    validateStep,
    type WizardData,
    type WizardStep,
} from './wizard/wizardTypes';

interface Props {
    automation?: DmAutomationDto;
    onExit: () => void;
}

interface SectionProps {
    num: number;
    title: string;
    desc?: string;
    children: React.ReactNode;
}

const Section = ({ num, title, desc, children }: SectionProps) => (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 2 }}>
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: ACCENT,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                        mt: '2px',
                    }}
                >
                    {num}
                </Box>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
                        {title}
                    </Typography>
                    {desc && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {desc}
                        </Typography>
                    )}
                </Box>
            </Stack>
            {children}
        </CardContent>
    </Card>
);

const AutomationFormPage = ({ automation, onExit }: Props) => {
    const isEditMode = Boolean(automation);
    const [data, setData] = useState<WizardData>(() =>
        automation ? dtoToWizardData(automation) : EMPTY_WIZARD_DATA,
    );
    const create = useCreateAutomation();
    const update = useUpdateAutomation();
    const limitQuery = useAutomationLimit({ enabled: !isEditMode });
    const remaining = limitQuery.data?.remaining ?? null;
    const isPending = create.isPending || update.isPending;

    const updateData = (patch: Partial<WizardData>) => setData((prev) => ({ ...prev, ...patch }));

    // 실제 생성/수정 실행
    const runSubmit = async () => {
        try {
            if (automation) {
                await update.mutateAsync({ id: automation.id, body: buildUpdatePayload(data) });
                toast.success('자동화를 수정했어요');
            } else {
                await create.mutateAsync(buildCreatePayload(data));
                toast.success('자동화가 만들어졌어요 🚀');
            }
            onExit();
        } catch (e: any) {
            toast.error(e?.data?.message || (isEditMode ? '자동화 수정에 실패했어요' : '자동화 생성에 실패했어요'));
        }
    };

    const handleSubmit = async () => {
        for (let s = 1 as WizardStep; s <= 5; s = (s + 1) as WizardStep) {
            if (!isStepApplicable(s, data.triggerType)) continue;
            const err = validateStep(s, data);
            if (err) {
                toast.warning(err);
                return;
            }
        }
        // 기존 자동화 수정은 차감 없음 → 바로 실행
        if (isEditMode) {
            runSubmit();
            return;
        }
        // 신규 생성은 이용권 1개 소모(복구 불가) → 확인 다이얼로그
        const remainText = remaining !== null ? ` (남은 이용권: ${remaining}개)` : '';
        dialogOpen({
            message: `이 자동화를 만들면 <b>이용권 1개가 차감</b>됩니다${remainText}.<br/>삭제하거나 꺼도 사용한 이용권은 <b>복구되지 않으니</b> 신중히 만들어 주세요.<br/><br/>계속할까요?`,
            confirmText: '만들기',
            closeText: '취소',
            isAllowHtml: true,
            onConfirmed: runSubmit,
        });
    };

    // 적용 가능한 섹션을 순서대로 모으되 동적 번호 부여
    let n = 0;
    const sections: { key: string; num: number; title: string; desc?: string; node: React.ReactNode }[] = [];
    sections.push({
        key: 'trigger',
        num: ++n,
        title: '어떤 자동화를 추가하시겠어요?',
        desc: '원하는 자동화를 골라주세요. 고르면 아래에 동작 방식이 안내돼요.',
        node: (
            <>
                <Step1Trigger compact data={data} onChange={updateData} />
                {data.triggerType !== '' && <TriggerExplainer type={data.triggerType} />}
            </>
        ),
    });
    // 신규 생성 시 트리거 미선택이면 이후 섹션 숨김 (사용자 흐름 가이드)
    const hasTrigger = data.triggerType !== '';
    if (hasTrigger) {
        sections.push({
            key: 'name',
            num: ++n,
            title: '이 자동화의 이름을 정해주세요',
            desc: '나만 보는 이름이에요. 알아보기 쉽게 적으면 돼요.',
            node: <Step2Name compact data={data} onChange={updateData} />,
        });
        if (isStepApplicable(3, data.triggerType)) {
            sections.push({
                key: 'keywords',
                num: ++n,
                title:
                    data.triggerType === 'STORY_REPLY'
                        ? '어떤 단어가 포함된 답장만 받을까요? (선택)'
                        : '어떤 특정 단어(키워드)를 감지할까요?',
                desc:
                    data.triggerType === 'STORY_REPLY'
                        ? '비워두면 모든 스토리 답장에 응답해요. 단어를 넣으면 그 단어가 포함된 답장만 받아요.'
                        : '상대(팔로워)가 이 단어 중 하나라도 쓰면 자동화가 켜져요. (내가 보내는 게 아니에요)',
                node: <Step3Keywords compact data={data} onChange={updateData} />,
            });
        }
        if (isStepApplicable(4, data.triggerType)) {
            sections.push({
                key: 'target',
                num: ++n,
                title: '어떤 게시물에 적용할까요?',
                desc: '자동화가 실행될 게시물 범위를 정해주세요.',
                node: <Step4Target compact data={data} onChange={updateData} />,
            });
        }
        sections.push({
            key: 'message',
            num: ++n,
            title: '어떤 메시지를 보낼까요?',
            desc: '키워드를 쓴 사람에게 자동으로 전송될 내용이에요. 버튼과 링크도 함께 넣을 수 있어요.',
            node: <Step5Message compact data={data} onChange={updateData} />,
        });
    }

    return (
        <Box>
            <PageHeader
                title={isEditMode ? '자동화 수정' : '새 자동화 만들기'}
                desc={
                    isEditMode
                        ? '수정할 항목을 바꾸고 저장을 눌러주세요. 언제든 다시 고칠 수 있어요.'
                        : '원하는 자동화를 고르고 항목을 채워주세요. 활성화하면 자동으로 DM이 나가요.'
                }
                action={
                    <Button
                        onClick={onExit}
                        startIcon={<BackIcon />}
                        variant="outlined"
                        disabled={isPending}
                        sx={{ textTransform: 'none', cursor: 'pointer', borderRadius: 2 }}
                    >
                        목록으로
                    </Button>
                }
            />

            <Stack spacing={2}>
                {sections.map((s) => (
                    <Section key={s.key} num={s.num} title={s.title} desc={s.desc}>
                        {s.node}
                    </Section>
                ))}

                {!hasTrigger && (
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                        먼저 위에서 자동화를 하나 골라주세요. 그러면 나머지 항목이 나타나요.
                    </Alert>
                )}
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1.5}
                sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}
            >
                <Button
                    onClick={onExit}
                    variant="outlined"
                    disabled={isPending}
                    sx={{ textTransform: 'none', borderRadius: 2, cursor: 'pointer' }}
                >
                    취소
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isPending || !hasTrigger}
                    startIcon={
                        isPending ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : isEditMode ? (
                            <SaveIcon />
                        ) : (
                            <LaunchIcon />
                        )
                    }
                    disableElevation
                    sx={{
                        bgcolor: ACCENT,
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2.5,
                        cursor: 'pointer',
                        transition: 'background-color 120ms ease, transform 120ms ease',
                        willChange: 'transform',
                        '&:hover': { bgcolor: ACCENT_HOVER, transform: 'translateY(-1px)' },
                        '&:active': { transform: 'translateY(0)' },
                        '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                    }}
                >
                    {isEditMode ? '저장' : '활성화하고 시작'}
                </Button>
            </Stack>
        </Box>
    );
};

export default AutomationFormPage;
